import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

function CouponManagementSection({ siteTimezone, formatDate, formatTime, formatDateTime }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'expired'
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    type: 'percentage', // 'percentage' or 'fixed'
    value: '',
    minOrder: '',
    expiryDate: '',
    usageLimit: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/coupons?${queryString}` : '/admin/coupons';
      
      const response = await axios.get(url);
      setCoupons(response.data.coupons || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }
  };
  
  // Refetch when search or filter changes
  useEffect(() => {
    fetchCoupons();
  }, [searchQuery, statusFilter]);

  const handleAddCoupon = async () => {
    if (!newCoupon.code) {
      toast.error('Please fill in coupon code');
      return;
    }
    
    if (newCoupon.type !== 'free_shipping' && !newCoupon.value) {
      toast.error('Please fill in discount value');
      return;
    }

    setLoading(true);
    try {
      let discountValue = 0;
      if (newCoupon.type === 'free_shipping') {
        discountValue = 0;
      } else if (newCoupon.type === 'fixed') {
        discountValue = Math.round(parseFloat(newCoupon.value) * 100); // Convert dollars to cents
      } else {
        discountValue = parseFloat(newCoupon.value); // Percentage stays as is
      }
      
      const payload = {
        code: newCoupon.code.toUpperCase(),
        discount_type: newCoupon.type,
        discount_value: discountValue,
        min_order_value: newCoupon.minOrder ? parseInt(newCoupon.minOrder) * 100 : 0,
        max_uses: newCoupon.usageLimit ? parseInt(newCoupon.usageLimit) : null,
        expires_at: newCoupon.expiryDate ? new Date(newCoupon.expiryDate).toISOString() : null,
        active: true
      };
      
      console.log('Sending coupon:', payload);
      await axios.post('/admin/coupons', payload);
      toast.success('✅ Coupon created successfully');
      setNewCoupon({
        code: '', type: 'percentage', value: '', minOrder: '', expiryDate: '', usageLimit: ''
      });
      fetchCoupons();
    } catch (error) {
      console.error('Failed to create coupon:', error);
      const message = error.response?.data?.detail || 'Failed to create coupon';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const startEditingCoupon = (coupon) => {
    setEditingCoupon(coupon.id);
    setEditValues({
      code: coupon.code,
      type: coupon.discount_type,
      value: coupon.discount_value.toString(),
      minOrder: (coupon.min_order_value / 100).toString(),
      expiryDate: coupon.expires_at ? new Date(coupon.expires_at).toISOString().split('T')[0] : '',
      usageLimit: coupon.max_uses?.toString() || ''
    });
  };

  const handleUpdateCoupon = async (couponId) => {
    setLoading(true);
    try {
      let discountValue = 0;
      if (editValues.type === 'free_shipping') {
        discountValue = 0;
      } else if (editValues.type === 'fixed') {
        discountValue = Math.round(parseFloat(editValues.value) * 100); // Convert dollars to cents
      } else {
        discountValue = parseFloat(editValues.value); // Percentage stays as is
      }
      
      await axios.put(`/admin/coupons/${couponId}`, {
        code: editValues.code.toUpperCase(),
        discount_type: editValues.type,
        discount_value: discountValue,
        min_order_value: editValues.minOrder ? parseInt(editValues.minOrder) * 100 : 0,
        max_uses: editValues.usageLimit ? parseInt(editValues.usageLimit) : null,
        expires_at: editValues.expiryDate ? new Date(editValues.expiryDate).toISOString() : null
      });
      toast.success('✅ Coupon updated successfully');
      setEditingCoupon(null);
      setEditValues({});
      fetchCoupons();
    } catch (error) {
      console.error('Failed to update coupon:', error);
      toast.error('Failed to update coupon');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`/admin/coupons/${couponId}`);
      toast.success('✅ Coupon deleted successfully');
      fetchCoupons();
    } catch (error) {
      console.error('Failed to delete coupon:', error);
      toast.error('Failed to delete coupon');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Coupon Management</h2>
        <p className="text-gray-600">Create and manage discount coupons</p>
      </div>

      {/* Add New Coupon */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Coupon</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
            <input
              type="text"
              placeholder="e.g., SAVE10"
              value={newCoupon.code}
              onChange={(e) => setNewCoupon(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="coupon-code"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
            <select
              value={newCoupon.type}
              onChange={(e) => setNewCoupon(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="coupon-type"
            >
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed Amount ($)</option>
              <option value="free_shipping">Free Shipping</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {newCoupon.type === 'free_shipping' ? 'Value (leave 0)' : newCoupon.type === 'percentage' ? 'Percentage' : 'Amount ($)'}
            </label>
            <input
              type="number"
              placeholder={newCoupon.type === 'percentage' ? '10' : '5.00'}
              value={newCoupon.value}
              onChange={(e) => setNewCoupon(prev => ({ ...prev, value: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="coupon-value"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Order ($)</label>
            <input
              type="number"
              placeholder="25.00"
              value={newCoupon.minOrder}
              onChange={(e) => setNewCoupon(prev => ({ ...prev, minOrder: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="coupon-min-order"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
            <input
              type="date"
              value={newCoupon.expiryDate}
              onChange={(e) => setNewCoupon(prev => ({ ...prev, expiryDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="coupon-expiry"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usage Limit</label>
            <input
              type="number"
              placeholder="100"
              value={newCoupon.usageLimit}
              onChange={(e) => setNewCoupon(prev => ({ ...prev, usageLimit: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="coupon-usage-limit"
            />
          </div>
        </div>
        
        <div className="mt-4">
          <Button 
            onClick={handleAddCoupon}
            disabled={loading}
            className="bg-custom-orange hover:bg-custom-orange"
            data-testid="create-coupon-button"
          >
            {loading ? 'Creating...' : 'Create Coupon'}
          </Button>
        </div>
      </Card>

      {/* Existing Coupons */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Coupons</h3>
        </div>
        
        {/* Search and Filter */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by code or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Coupons</option>
              <option value="active">Active Only</option>
              <option value="expired">Expired Only</option>
            </select>
          </div>
        </div>
        
        {coupons.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No coupons created yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2">Code</th>
                  <th className="text-left py-3 px-2">Type</th>
                  <th className="text-left py-3 px-2">Value</th>
                  <th className="text-left py-3 px-2">Min Order</th>
                  <th className="text-left py-3 px-2">Expires</th>
                  <th className="text-left py-3 px-2">Used</th>
                  <th className="text-left py-3 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon.id} className="border-b border-gray-100 hover:bg-gray-50">
                    {editingCoupon === coupon.id ? (
                      // Edit mode
                      <>
                        <td className="py-3 px-2">
                          <input
                            type="text"
                            value={editValues.code}
                            onChange={(e) => setEditValues(prev => ({ ...prev, code: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="py-3 px-2">
                          <select
                            value={editValues.type}
                            onChange={(e) => setEditValues(prev => ({ ...prev, type: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="percentage">Percentage</option>
                            <option value="fixed">Fixed</option>
                            <option value="free_shipping">Free Shipping</option>
                          </select>
                        </td>
                        <td className="py-3 px-2">
                          <input
                            type="number"
                            value={editValues.value}
                            onChange={(e) => setEditValues(prev => ({ ...prev, value: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            step="0.01"
                          />
                        </td>
                        <td className="py-3 px-2">
                          <input
                            type="number"
                            value={editValues.minOrder}
                            onChange={(e) => setEditValues(prev => ({ ...prev, minOrder: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            step="0.01"
                          />
                        </td>
                        <td className="py-3 px-2">
                          <input
                            type="date"
                            value={editValues.expiryDate}
                            onChange={(e) => setEditValues(prev => ({ ...prev, expiryDate: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="py-3 px-2">
                          <input
                            type="number"
                            value={editValues.usageLimit}
                            onChange={(e) => setEditValues(prev => ({ ...prev, usageLimit: e.target.value }))}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Unlimited"
                          />
                        </td>
                        <td className="py-3 px-2 space-x-1">
                          <Button 
                            size="sm" 
                            onClick={() => handleUpdateCoupon(coupon.id)}
                            disabled={loading}
                            className="bg-custom-orange hover:bg-custom-orange text-white"
                          >
                            Save
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditingCoupon(null);
                              setEditValues({});
                            }}
                          >
                            Cancel
                          </Button>
                        </td>
                      </>
                    ) : (
                      // View mode
                      <>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{coupon.code}</span>
                            {coupon.is_expired && (
                              <Badge className="bg-red-500 text-white">Expired</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline">
                            {coupon.discount_type === 'percentage' ? 'Percentage' : 
                             coupon.discount_type === 'fixed' ? 'Fixed' : 'Free Shipping'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          {coupon.discount_type === 'free_shipping' ? 'Free Shipping' :
                           coupon.discount_type === 'percentage' 
                            ? `${coupon.discount_value}%` 
                            : `$${(coupon.discount_value / 100).toFixed(2)}`}
                        </td>
                        <td className="py-3 px-2">
                          ${(coupon.min_order_value / 100).toFixed(2)}
                        </td>
                        <td className="py-3 px-2">
                          {coupon.expires_at 
                            ? (formatDate ? formatDate(coupon.expires_at, siteTimezone) : new Date(coupon.expires_at).toLocaleDateString())
                            : 'Never'}
                        </td>
                        <td className="py-3 px-2">
                          {coupon.used_count || 0} / {coupon.max_uses || '∞'}
                        </td>
                        <td className="py-3 px-2 space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => startEditingCoupon(coupon)}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}



export default CouponManagementSection;
